import { Box, Flex } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import useCreateRateio from "core/features/rateio/hooks/useCreateRateio";
import useEditRateio from "core/features/rateio/hooks/useEditRateio";
import useGetContasRateio from "core/features/rateio/hooks/useGetContasRateio";
import useRemoverContaRateio from "core/features/rateio/hooks/useRemoverContaRateio";
import { RateioForm } from "core/features/rateio/typings";
import { showConfirm } from "infra/helpers/alerta";
import { useState } from "react";
import { useForm } from "react-hook-form";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import ContaTable from "ui/components/ContaTable";
import Field from "../Field";
import FormRateioHeader from "./FormRateioHeader";

function FormRateio({
  year,
  month,
  nomeRateio,
  onSuccess,
  onClose,
  rateioEditId,
  rateioAberto = true,
}: {
  year: string;
  month: string;
  nomeRateio?: string;
  onSuccess?: (data: any) => Promise<void>;
  onClose?: () => void;
  rateioEditId?: string;
  rateioAberto?: boolean;
}) {
  const [_rateioAberto, setRateioAberto] = useState(rateioAberto);
  const [rateioId, setRateioId] = useState(rateioEditId);

  const hooksArgs = { id: rateioId, year, month };

  const {
    data: contasRateio,
    isLoading: isLoadingContasRateio,
    isFetching: isFetchingContasRateio,
    refetch: refetchContas,
  } = useGetContasRateio(hooksArgs);

  const { mutate: removerContaRateio, isLoading: isLoadingRemoverConta } =
    useRemoverContaRateio(hooksArgs);

  const [enabledEdit, setEnabledEdit] = useState(false);
  const { control, handleSubmit, reset } = useForm<RateioForm>({
    reValidateMode: "onChange",
    mode: "onChange",
    defaultValues: {
      nome: nomeRateio,
    },
  });

  const _onSuccess = (id: string) => {
    setRateioId(id);
    onSuccess?.(id);
  };

  const { isLoading: isLoadingCreate, mutate: handleCreate } =
    useCreateRateio(_onSuccess);

  const { isLoading: isLoadingEdit, mutate: handleEdit } =
    useEditRateio(_onSuccess);

  const onSubmit = async (data: RateioForm) => {
    if (rateioEditId) {
      handleEdit({ id: rateioEditId, data });
    } else {
      handleCreate({ data });
    }
  };

  const _isLoadingData = isLoadingContasRateio || isFetchingContasRateio;

  const isLoadingMutate =
    isLoadingCreate || isLoadingRemoverConta || isLoadingEdit;

  return (
    <form onSubmit={(ev) => handleSubmit(onSubmit)(ev)}>
      <Flex flexDir="column" flex={1} h="100%" flexGrow={1}>
        {rateioId ? (
          <>
            <Flex mb={4} h="40px">
              <FormRateioHeader
                onEnableEdit={() => setEnabledEdit((state) => !state)}
                enabledEdit={enabledEdit}
                control={control}
                loading={_isLoadingData || isLoadingRemoverConta}
                rateioId={rateioId}
                year={year}
                month={month}
                refetchContas={refetchContas}
                contasRateio={contasRateio}
                rateioAberto={_rateioAberto}
                onCloseRateio={() => setRateioAberto(false)}
              />
            </Flex>

            <Flex
              flexDir="column"
              maxH="55vh"
              overflowY="auto"
              className="custom-scroll-bar"
            >
              <ContaTable
                data={
                  contasRateio
                    ? {
                        items: contasRateio,
                        meta: {
                          currentPage: 1,
                          itemCount: contasRateio.length,
                          itemsPerPage: contasRateio.length,
                          totalItems: contasRateio.length,
                          totalPages: 1,
                        },
                      }
                    : undefined
                }
                searchValue={""}
                onSearch={() => null}
                enabledSearch={false}
                isLoading={_isLoadingData || isLoadingRemoverConta}
                actions={
                  _rateioAberto
                    ? [
                        {
                          title: "Remover conta",
                          onClick: ({ id }) =>
                            showConfirm(
                              "Deseja realmente remover essa conta desse rateio?"
                            ).then(({ isConfirmed }) => {
                              isConfirmed &&
                                removerContaRateio(
                                  {
                                    data: {
                                      contas: [id],
                                    },
                                  },
                                  {
                                    onSuccess: () => refetchContas(),
                                  }
                                );
                            }),
                        },
                      ]
                    : undefined
                }
              />
            </Flex>
          </>
        ) : (
          <>
            <Text as="h3" fontWeight="500" fontSize={24} mb={4}>
              Nome para esse rateio
            </Text>
            <Field
              control={control}
              name="nome"
              rules={{ required: true }}
              inputProps={{ placeholder: "Nome do rateio" }}
            />
          </>
        )}

        <Box
          pt="32px"
          justifyContent="flex-end"
          d="flex"
          gridGap="8px"
          flexDir="row"
          marginTop="auto"
          pb={"16px"}
        >
          <OutlineButton
            disabled={isLoadingMutate || _isLoadingData}
            onClick={() => {
              onClose?.();
              reset();
            }}
          >
            Cancelar
          </OutlineButton>

          <PrimaryButton
            type="button"
            isLoading={isLoadingMutate || _isLoadingData}
            onClick={() => {
              if (rateioId && !_rateioAberto) {
                onClose?.();
              } else {
                handleSubmit(onSubmit)();
              }
            }}
            // disabled={
            //   !formState.isValid ||
            //   isLoadingMutate ||
            //   _isLoadingData ||
            //   !contasRateio?.length
            // }
            minWidth={"120px"}
          >
            {rateioId ? "Concluir" : "Próximo"}
          </PrimaryButton>
        </Box>
      </Flex>
    </form>
  );
}

export default FormRateio;
